/* eslint-disable @typescript-eslint/only-throw-error */
import { type LoaderFunction, redirect, replace } from 'react-router'
import { links } from '@/utils/link'
import { type Id } from '@/infra/id'
import type { CreateJobRoutesOptions } from '@/features/jobs/routes'
import { BookkeepingJobDetailsViewModel } from '@/features/jobs/details/bookkeeping-type/BookkeepingJobDetailsViewModel'
import type { BookkeepingStore } from '@/features/jobs/bookkeeping/BookkeepingStore'
import type { BookkeepingProgressionStore } from '@/features/jobs/bookkeeping/BookkeepingProgressionStore'
import type { BookkeepingViewModel } from '@/features/jobs/details/bookkeeping-type/BookkeepingViewModel'
import { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { ConsultationJobDetailsViewModel } from '@/features/jobs/details/consultation-type/ConsultationJobDetailsViewModel'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'

/**
 * Creates a loader function for the job details view
 * that checks the status of the job and redirects to the draft
 * view if the job is currently a draft.
 *
 * @param options
 */
export function createJobDetailsViewLoader(
  options: CreateJobRoutesOptions,
): LoaderFunction<{ id: string }> {
  return async ({ params, request: { signal, url } }) => {
    const jobGist = await checkAndAndFetchJob(params.id, options, signal)
    if (!jobGist) {
      return
    }

    if (jobGist.status === 'UnderConstruction') {
      throw redirect(links.editDraftJob(jobGist.id))
    }

    // Retrieve the submitted job
    const internal = await options.jobs.internal()
    const submittedJob = await internal.submittedJobStore.fetchJob(jobGist.id)
    if (!submittedJob) {
      throw redirect(links.home())
    }

    const vm = await internal.acquireJobDetailsViewModel(submittedJob, signal)
    await vm.fetch(signal)

    // Tax jobs may be in info gathering; if so, we'll need to redirect.
    if (vm.typeVm instanceof TaxJobDetailsViewModel) {
      if (submittedJob.status === 'InfoGathering') {
        throw redirect(links.taxJobInfoGathering(jobGist.id))
      }
    }

    // Consultation jobs may be in info gathering; if so, we'll need to redirect.
    if (vm.typeVm instanceof ConsultationJobDetailsViewModel) {
      if (submittedJob.status === 'InfoGathering') {
        throw redirect(links.consultationJobInfoGathering(jobGist.id))
      }
    }

    // Bookkeeping jobs receive a redirect from
    // the bookkeeping model for a new platform connection.
    if (vm.typeVm instanceof BookkeepingJobDetailsViewModel) {
      const { bookkeepingStore, bookkeepingProgressionStore } = internal

      const targetUrl = await maybeCompleteBookkeepingPlatformConnection(
        url,
        jobGist.id,
        vm.typeVm.bookkeepingVm,
        bookkeepingStore,
        bookkeepingProgressionStore,
      )

      if (targetUrl) {
        return replace(targetUrl)
      }
    }

    return { vm }
  }
}

/**
 * Possible tabs.
 */
type JobTab = 'overview' | 'messages' | 'documents' | 'billing'

/**
 * Creates a loader function that verifies that the user can view the
 * requested tab, and redirects otherwise.
 *
 * @param options
 * @param tab
 */
export function createJobDetailsTabLoader(
  options: CreateJobRoutesOptions,
  tab: JobTab,
): LoaderFunction<unknown> {
  return async ({ params, request: { signal } }) => {
    const jobGist = await checkAndAndFetchJob(params.id, options, signal)
    if (!jobGist) {
      return
    }

    const internal = await options.jobs.internal()
    const submittedJob = await internal.submittedJobStore.fetchJob(jobGist.id)
    if (!submittedJob) {
      return
    }

    const vm = await internal.acquireJobDetailsViewModel(submittedJob, signal)

    switch (tab) {
      case 'overview':
        if (!vm.isOverviewTabEnabled) {
          throw redirect(links.jobDetailsMessages(jobGist.id))
        }
        break
      case 'billing':
        if (!vm.isBillingTabEnabled) {
          throw redirect(links.jobDetails(jobGist.id))
        }
        break
    }
  }
}

/**
 * Creates a loader function for the tax info gathering view
 * that checks the status of the job and redirects to the job details
 * view if not currently in info gathering.
 *
 * @param options
 */
export function createTaxInfoGatheringViewLoader(
  options: CreateJobRoutesOptions,
): LoaderFunction<{ id: string }> {
  return createJobDetailsInfoGatheringRedirectionLoader(
    options,
    (vm) => vm.typeVm instanceof TaxJobDetailsViewModel,
  )
}

/**
 * Creates a loader function for the consultation info gathering view
 * that checks the status of the job and redirects appropriately.
 *
 * @param options
 */
export function createConsultationInfoGatheringViewLoader(
  options: CreateJobRoutesOptions,
): LoaderFunction<{ id: string }> {
  return createJobDetailsInfoGatheringRedirectionLoader(
    options,
    (vm) =>
      vm.typeVm instanceof ConsultationJobDetailsViewModel &&
      !!vm.typeVm.consultation,
  )
}

/**
 * Creates a loader function for the draft job view
 * that checks the status of the job and redirects to the
 * job details view if the job is not a draft.
 *
 * @param options
 */
export function createDraftJobViewLoader(
  options: CreateJobRoutesOptions,
): LoaderFunction<unknown> {
  return async ({ params, request: { signal } }) => {
    const jobGist = await checkAndAndFetchJob(params.id, options, signal)
    if (!jobGist) {
      return
    }

    if (jobGist.status !== 'UnderConstruction') {
      throw redirect(links.jobDetails(jobGist.id))
    }

    const internal = await options.jobs.internal()
    const job = await internal.draftJobStore.fetchDraftJob(params.id)
    if (!job) {
      return
    }

    // Shouldn't happen but just to be safe
    if (job.isSubmitted) {
      throw redirect(links.jobDetails(jobGist.id))
    }

    const vm = await internal.acquireDraftJobViewModel(job, signal)
    return { vm }
  }
}

/**
 * Creates a loader function for the legacy job route that
 * redirects to the new job details view.
 */
export function createRedirectFromLegacyRouteLoader(): LoaderFunction<unknown> {
  return ({ params: { id } }) => replace(links.jobDetails(id))
}

/**
 * Creates a loader for info gathering that redirects to job
 * details if the predicate fails.
 *
 * @param options
 * @param predicate
 */
function createJobDetailsInfoGatheringRedirectionLoader(
  options: CreateJobRoutesOptions,
  predicate: (vm: JobDetailsViewModel) => boolean,
): LoaderFunction<{ id: string }> {
  return async ({ params, request: { signal } }) => {
    const jobGist = await checkAndAndFetchJob(params.id, options, signal)
    if (!jobGist) {
      return
    }

    if (jobGist.status !== 'InfoGathering') {
      throw redirect(links.jobDetails(jobGist.id))
    }

    // Retrieve the submitted job
    const internal = await options.jobs.internal()
    const submittedJob = await internal.submittedJobStore.fetchJob(jobGist.id)
    if (!submittedJob) {
      throw redirect(links.home())
    }

    const vm = await internal.acquireJobDetailsViewModel(submittedJob, signal)
    if (!predicate(vm)) {
      throw redirect(links.jobDetails(jobGist.id))
    }

    await vm.fetch(signal)

    return { vm }
  }
}

/**
 * Completes the bookkeeping platform connection flow.
 */
async function maybeCompleteBookkeepingPlatformConnection(
  url: string,
  jobId: Id,
  bookkeepingVm: BookkeepingViewModel,
  bookkeepingStore: BookkeepingStore,
  bookkeepingProgressionStore: BookkeepingProgressionStore,
) {
  const targetUrl = new URL(url)
  const params = targetUrl.searchParams
  const connectionId = params.get('connection_id')
  const bookkeepingPlatform = params.get('bookkeeping_platform')

  const progressionInfo =
    await bookkeepingProgressionStore.fetchBookkeepingProgressionInfo(jobId)

  // Removes the search params from target URL.
  const removeSearchParams = () => {
    targetUrl.searchParams.delete('connection_id')
    targetUrl.searchParams.delete('bookkeeping_platform')
  }

  if (connectionId && bookkeepingPlatform === 'quickbooks') {
    await bookkeepingStore.provideQuickBooksPlatformConnection(
      jobId,
      connectionId,
    )

    bookkeepingVm.bookkeepingConfirmAccessDialogVm.show(progressionInfo)
    removeSearchParams()

    return targetUrl.toString()
  }

  if (connectionId && bookkeepingPlatform === 'xero') {
    await bookkeepingStore.provideXeroPlatformConnection(jobId, connectionId)

    bookkeepingVm.bookkeepingConfirmAccessDialogVm.show(progressionInfo)
    removeSearchParams()

    return targetUrl.toString()
  }
}

/**
 * Checks that we are authenticated and fetches the job.
 * Handles redirecting to the homepage and showing the 404 page
 * when appropriate.
 *
 * @param jobId
 * @param options
 * @param signal
 */
async function checkAndAndFetchJob(
  jobId: Id,
  options: CreateJobRoutesOptions,
  signal?: AbortSignal,
) {
  // Wait for auth
  const { authenticated, workspace } =
    await options.waitForAuthResolution(signal)
  if (!authenticated) {
    // This means we'll be shown the login page.
    return null
  }

  const jobGist = await options.jobs.fetchJobGist(jobId)
  if (!jobGist) {
    throw new Response(null, { status: 404 })
  }

  if (workspace === null) {
    // No workspace selected; select one using the one from the job.
    await options.selectWorkspace(jobGist.workspaceId, signal)
  } else if (jobGist.workspaceId !== workspace.id) {
    throw redirect(links.home())
  }

  return jobGist
}
